<template>
  <div>
    <p class="text-body-1 mb-2">
      {{ label }}
    </p>
    
    <!-- Release Type Dropdown -->
    <v-select
      v-model="releaseType"
      :items="releaseOptions"
      item-text="text"
      item-value="value"
      outlined
      dense
      hide-details="auto"
      class="mb-4 scheduler-field"
      @change="onReleaseTypeChange"
    >
      <template #selection="{ item }">
        <div class="d-flex align-center">
          <img 
            :src="item.icon" 
            :alt="item.text"
            class="mr-2"
            width="16"
            height="16"
          >
          <span>{{ item.text }}</span>
        </div>
      </template>
      <template #item="{ item }">
        <div class="d-flex align-center">
          <img 
            :src="item.icon" 
            :alt="item.text"
            class="mr-2"
            width="16"
            height="16"
          >
          <span>{{ item.text }}</span>
        </div>
      </template>
    </v-select>

    <!-- Date/Time Pickers (shown for Live and Scheduled) -->
    <v-row v-if="showDateTimePickers">
      <v-col cols="auto">
        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="dateDisplay"
              :placeholder="datePlaceholder"
              outlined
              dense
              hide-details="auto"
              readonly
              append-icon="mdi-calendar"
              v-bind="attrs"
              :error="hasError || !!dateValidationError"
              :error-messages="dateValidationError"
              :loading="isUpdating"
              class="scheduler-field"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="localValueDateOnly"
            :max="releaseType === 'live' ? maxDateForLive : undefined"
            :min="releaseType === 'scheduled' ? minDateForScheduled : undefined"
            @input="onDateSelected"
          />
        </v-menu>
      </v-col>
      <v-col cols="auto">
        <v-menu
          v-model="timeMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="localValueTimeOnly"
              :placeholder="timePlaceholder"
              outlined
              dense
              hide-details="auto"
              readonly
              append-icon="mdi-clock-time-four-outline"
              v-bind="attrs"
              :disabled="isUpdating"
              :error="hasError"
              :loading="isUpdating"
              class="scheduler-field"
              v-on="on"
            />
          </template>
          <v-time-picker
            v-model="localValueTimeOnly"
            format="24hr"
            @click:minute="onTimeSelected"
          />
        </v-menu>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ProductInfoFieldMixin from '@/mixins/ProductInfoFieldMixin';
import { formatDateForPicker } from '@/utils/dateHelper';
import { RELEASE_TYPES } from '@/utils/Const';
import liveIcon from '@/components/icons/publicationStatus/live.png';
import scheduledIcon from '@/components/icons/publicationStatus/scheduled.png';
import unlistedIcon from '@/components/icons/publicationStatus/unlisted.png';

export default {
  name: 'LocalBookReleaseScheduler',
  mixins: [ProductInfoFieldMixin],
  props: {
    label: {
      type: String,
      default: 'Schedule Book Release'
    },
    datePlaceholder: {
      type: String,
      default: 'MM / DD / YYYY'
    },
    timePlaceholder: {
      type: String,
      default: '00:00'
    },
    previewMode: {
      type: Boolean,
      default: false
    },
  },
  emits: ['valid', 'release-type-changed'],
  data() {
    return {
      dateMenu: false,
      timeMenu: false,
      releaseType: RELEASE_TYPES.SCHEDULED,
      dateValidationError: ''
    };
  },
  computed: {
    releaseOptions() {
      return [
        {
          value: RELEASE_TYPES.LISTED, 
          text: this.$t('bookInfo.listed'),
          icon: liveIcon
        },
        {
          value: RELEASE_TYPES.SCHEDULED,
          text: this.$t('bookInfo.scheduled'),
          icon: scheduledIcon
        },
        {
          value: RELEASE_TYPES.UNLISTED,
          text: this.$t('bookInfo.unlisted'),
          icon: unlistedIcon
        }
      ];
    },
    showDateTimePickers() {
      return this.releaseType === RELEASE_TYPES.LISTED || this.releaseType === RELEASE_TYPES.SCHEDULED;
    },
    maxDateForLive() {
      // For live option, max date is today
      return formatDateForPicker(new Date());
    },
    minDateForScheduled() {
      // For scheduled option, min date is tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return formatDateForPicker(tomorrow);
    },
    localValueDateOnly: {
      get() {
        if (this.localValue && this.localValue !== '#') {
          // Extract date part from UTC string like "2025-05-30T00:00:00+00:00"
          return this.localValue.split('T')[0];
        }
        return null;
      },
      set(value) {
        this.validateDate(value);
        this.updateDateTimeFromParts(value, this.localValueTimeOnly);
      }
    },
    localValueTimeOnly: {
      get() {
        if (this.localValue && this.localValue !== '#') {
          // Extract time part from UTC string like "2025-05-30T14:30:00+00:00"
          const timePart = this.localValue.split('T')[1];
          return timePart ? timePart.split(':').slice(0, 2).join(':') : null;
        }
        return null;
      },
      set(value) {
        this.updateDateTimeFromParts(this.localValueDateOnly, value);
      }
    },
    dateDisplay() {
      if (!this.localValueDateOnly) return '';
      // Convert YYYY-MM-DD to locale date string
      const [year, month, day] = this.localValueDateOnly.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString();
    },
    isValid() {
      return this.releaseType === RELEASE_TYPES.UNLISTED ? true : (!!this.localValueDateOnly && !this.dateValidationError);
    }
  },
  watch: {
    isValid(newVal) {
      this.$emit('valid', newVal);
    },
    previewMode: {
      handler(newVal) {
        this.disableSave = newVal;
      },
      immediate: true
    }
  },
  mounted() {
    this.initializeReleaseTypeFromData();
    this.$emit('valid', this.isValid);
    this.$emit('release-type-changed', this.releaseType);
  },
  methods: {
    getTodayDateString() {
      const today = new Date();
      return today.getFullYear() + '-' + 
             String(today.getMonth() + 1).padStart(2, '0') + '-' + 
             String(today.getDate()).padStart(2, '0');
    },
    
    initializeReleaseTypeFromData() {
      if (!this.localValue || this.localValue === '#') {
        this.releaseType = RELEASE_TYPES.UNLISTED;
        return;
      }
      
      const dateStr = this.localValue.split('T')[0];
      if (!dateStr) {
        this.releaseType = RELEASE_TYPES.UNLISTED;
        return;
      }
      
      const todayStr = this.getTodayDateString();
      
      if (dateStr < todayStr) {
        this.releaseType = RELEASE_TYPES.LISTED;
      } else {
        this.releaseType = RELEASE_TYPES.SCHEDULED;
      }
    },

    onReleaseTypeChange(type) {
      
      this.dateValidationError = '';
      
      if (type === RELEASE_TYPES.UNLISTED) {
        this.onInput(null);
      } else if (type === RELEASE_TYPES.LISTED) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const dateStr = formatDateForPicker(yesterday);
        const timeStr = '00:00';
        this.updateDateTimeFromParts(dateStr, timeStr);
        this.validateDate(dateStr);
      } else if (type === RELEASE_TYPES.SCHEDULED) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dateStr = formatDateForPicker(tomorrow);
        const timeStr = '09:00';
        this.updateDateTimeFromParts(dateStr, timeStr);
        this.validateDate(dateStr);
      }
      
      this.$emit('release-type-changed', type);
      
      this.$nextTick(() => {
        this.$emit('valid', this.isValid);
      });
    },
    
    validateDate(dateStr) {
      if (!dateStr) {
        this.dateValidationError = '';
        return;
      }
      
      // Compare date strings directly (YYYY-MM-DD format)
      const todayStr = this.getTodayDateString();
      
      // Check against publication date
      const publishedOnStr = this.productInfoManager?.getData()?.book?.publishedOn;
      if (publishedOnStr) {
        const publishedDateStr = publishedOnStr.split('T')[0];
        if (dateStr < publishedDateStr) {
          this.dateValidationError = this.$t('admin.productsDatabase.importWizard.steps.release.availableFromMustNotBeBeforePublicationDate');
          return;
        }
      }
      
      if (this.releaseType === RELEASE_TYPES.LISTED && dateStr > todayStr) {
        this.dateValidationError = this.$t('admin.productsDatabase.importWizard.steps.release.liveDateMustBePast');
      } else if (this.releaseType === RELEASE_TYPES.SCHEDULED && dateStr <= todayStr) {
        this.dateValidationError = this.$t('admin.productsDatabase.importWizard.steps.release.scheduledDateMustBeFuture');
      } else {
        this.dateValidationError = '';
      }
    },
    
    updateDateTimeFromParts(date, time) {
      if (!date) {
        this.onInput(null);
        return;
      }
      
      const utcDateTimeString = date + 'T' + (time || '00:00') + ':00+00:00';
      this.onInput(utcDateTimeString);
    },
    
    onDateSelected() {
      this.dateMenu = false;
      this.validateDate(this.localValueDateOnly);
    },
    
    onTimeSelected() {
      this.timeMenu = false;
    },
    
    // Override mixin's validateValue to use our custom validation
    validateValue(value) {
      if (this.releaseType === RELEASE_TYPES.UNLISTED) {
        return true;
      }
      return !!value && !this.dateValidationError;
    }
  }
};
</script>

<style scoped>
.scheduler-field {
  width: 240px;
  max-width: 240px;
  min-width: 0;
}
</style>